import { create } from 'zustand';
import type { Ticket, TicketWithRelations } from '@asi/shared';
import { supabase } from '@/services/supabase';

interface AlertsState {
  tickets: Ticket[];
  selectedTicket: TicketWithRelations | null;
  loading: boolean;
  fetchTickets: (silent?: boolean) => Promise<void>;
  selectTicket: (id: string) => Promise<void>;
  subscribeToTickets: () => () => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  tickets: [],
  selectedTicket: null,
  loading: false,

  fetchTickets: async (silent = false) => {
    if (!silent) set({ loading: true });
    try {
      const { data } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      set({ tickets: data ?? [] });
    } finally {
      if (!silent) set({ loading: false });
    }
  },

  selectTicket: async (id) => {
    const { data } = await supabase
      .from('tickets')
      .select(`
        *,
        client:profiles!tickets_client_id_fkey(*),
        patrol:profiles!tickets_patrol_id_fkey(*),
        evidence(*),
        calls(*),
        logs:ticket_logs(*),
        messages:ticket_messages(id, type, body, created_at, sender_id, file_url, thumbnail_url, mime_type, sender:profiles!ticket_messages_sender_id_fkey(full_name, role))
      `)
      .eq('id', id)
      .single();
    set({ selectedTicket: data as TicketWithRelations | null });
  },

  subscribeToTickets: () => {
    const channel = supabase
      .channel(`tickets-realtime-${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tickets' },
        () => {
          get().fetchTickets(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
}));
